import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiX,
  FiHome,
  FiSend,
  FiSettings,
  FiPlusCircle,
  FiHelpCircle,
  FiInfo,
  FiShield,
  FiLogOut,
  FiArrowDownLeft
} from 'react-icons/fi';
import { RiSwapLine } from 'react-icons/ri';
import '../styles/Drawer.css';

const Drawer = ({ isOpen, onClose, title }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      icon: FiHome,
      title: 'Home',
      path: '/home',
      onClick: () => {
        navigate('/home');
        onClose();
      }
    },
    {
      icon: FiArrowDownLeft,
      title: 'Receive',
      path: '/receive',
      onClick: () => {
        navigate('/receive');
        onClose();
      }
    },
    {
      icon: FiSend,
      title: 'Send',
      path: '/send',
      onClick: () => {
        navigate('/send');
        onClose();
      }
    },
    {
      icon: RiSwapLine,
      title: 'Swap',
      path: '/swap',
      onClick: () => {
        navigate('/swap');
        onClose();
      }
    },
    {
      icon: FiPlusCircle,
      title: 'Custom Tokens',
      path: '/custom-tokens',
      onClick: () => {
        navigate('/custom-tokens');
        onClose();
      }
    }
  ];

  const settingsItems = [
    {
      icon: FiShield,
      title: 'Security',
      path: '/settings/security',
      onClick: () => {
        navigate('/settings/security');
        onClose();
      }
    },
    {
      icon: FiHelpCircle,
      title: 'Help Center',
      path: '/settings/help',
      onClick: () => {
        navigate('/settings/help');
        onClose();
      }
    },
    {
      icon: FiInfo,
      title: 'About',
      path: '/settings/about',
      onClick: () => {
        navigate('/settings/about');
        onClose();
      }
    }
  ];

  const isActive = (path) => location.pathname === path;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: { x: 0, opacity: 1 }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="drawer"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <motion.div
              className="active-particles"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5 }}
            >
              {[...Array(15)].map((_, index) => (
                <div key={index} className="particle" />
              ))}
            </motion.div>

            <div className="drawer-header">
              <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {title}
              </motion.h2>
              <motion.button
                className="drawer-close"
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX />
              </motion.button>
            </div>

            <motion.div
              className="drawer-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="drawer-section">
                <div className="section-title">Menu</div>
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.title}
                      className={"drawer-item " + (isActive(item.path) ? 'active' : '')}
                      onClick={item.onClick}
                      variants={itemVariants}
                      whileHover={{ x: 4, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Icon />
                      <span>{item.title}</span>
                    </motion.button>
                  );
                })}
              </div>

              <div className="drawer-section">
                <div className="section-title">Settings</div>
                {settingsItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.title}
                      className={"drawer-item " + (isActive(item.path) ? 'active' : '')}
                      onClick={item.onClick}
                      variants={itemVariants}
                      whileHover={{ x: 4, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Icon />
                      <span>{item.title}</span>
                    </motion.button>
                  );
                })}
              </div>

              <div className="drawer-footer">
                <motion.button
                  className="drawer-item logout"
                  onClick={() => {
                    navigate('/');
                    onClose();
                  }}
                  variants={itemVariants}
                  whileHover={{ x: 4, backgroundColor: 'rgba(255, 59, 48, 0.1)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiLogOut />
                  <span>Log Out</span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Drawer; 